import React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { EventBusy } from "@mui/icons-material";
import type { VenueBooking } from "./types";
import DialogHeader from "./DialogHeader";
import {
  buildVenueHireRefundMailto,
  formatSlotRange,
  formatVenueBookingPrice,
  isValidVenueBooking,
} from "./venueHireUtils";

interface CancelVenueBookingDialogProps {
  open: boolean;
  booking: VenueBooking | null;
  clubName: string;
  cancelling: boolean;
  onClose: () => void;
  onConfirmCancel: () => void;
}

const CancelVenueBookingDialog: React.FC<CancelVenueBookingDialogProps> = ({
  open,
  booking,
  clubName,
  cancelling,
  onClose,
  onConfirmCancel,
}) => {
  if (!open || !booking || !isValidVenueBooking(booking)) {
    return null;
  }

  const refundMailto = buildVenueHireRefundMailto({
    clubName,
    date: booking.date,
    startTime: booking.start_time,
    endTime: booking.end_time,
    price: booking.price,
    invoiceId: booking.invoice_id,
  });

  return (
    <Dialog
      open={open}
      onClose={() => !cancelling && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title={booking.paid ? "Request cancellation" : "Cancel booking"}
        subtitle={clubName}
        icon={<EventBusy color="warning" />}
        onClose={onClose}
        closeDisabled={cancelling}
      />
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            {booking.paid
              ? "This booking has been paid. To cancel and request a refund, please email Matt with the details below."
              : "You are about to cancel the following venue slot. The slot will become available for others to book."}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatSlotRange(booking.date, booking.start_time, booking.end_time)}
          </Typography>
          <Typography variant="body1">
            {formatVenueBookingPrice(booking.price)}
          </Typography>
          {booking.paid ? (
            <>
              <Alert severity="info">
                Refunds are handled manually. Your slot will remain booked until
                Matt processes your request.
              </Alert>
              <Link
                href={refundMailto}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 600 }}
              >
                Email matt@easyagility.co.uk to request a refund
              </Link>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Any unpaid invoice for this booking will be removed.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={cancelling}>
          {booking.paid ? "Close" : "Keep booking"}
        </Button>
        {!booking.paid && (
          <Button
            variant="contained"
            color="warning"
            onClick={onConfirmCancel}
            disabled={cancelling}
            startIcon={
              cancelling ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {cancelling ? "Cancelling…" : "Cancel booking"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CancelVenueBookingDialog;
